import { useFlags } from '@atlaskit/flag';
import { LocationsApiService } from 'core/api';
import { ILocationDao } from 'core/api/types/locations.interface';
import { useDialog } from 'core/providers/DialogProvider';
import { PageElement } from 'core/utilities/interface-helpers';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import AddLocationDialog from './dialogs/add-location-dialog';
import UpdateLocationDialog from './dialogs/update-location-dialog';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { useNavigate } from 'react-router-dom';
import { deleteItem, deleteItems } from 'core/utilities/api-wrapper';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@atlaskit/checkbox';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import ExportDialog from 'modules/dialogs/export-dialog';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';
import { useSharedData } from 'core/providers/SharedDataProvider';

const LocationsTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Location', key: 'name', templateId: 'text', width: 15 },
  { label: 'Postcode', key: 'postcode', templateId: 'postcode', width: 10 },
  { label: 'Phone number', key: 'phoneNumber', templateId: 'text', width: 15 },
  { label: 'Email address', key: 'emailAddress', templateId: 'text', width: 15 },
  { label: 'Status', key: 'active', templateId: 'boolean', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const LocationsList = () => {
  const dialog = useDialog();
  const flags = useFlags();
  const navigate = useNavigate();
  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const { locations, loading } = useSharedData();
  const isLoading = loading.locations;
  const pageSize = 100;
  const [page, setPage] = useState(1);
  const sortedNormalisedLocations = useMemo(() => {
    return [...(locations ?? [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((l) => ({
        ...l,
        nameLower: l.name.toLowerCase(),
        postcodeLower: l.address.fullAddress.postcode.toLowerCase(),
      }));
  }, [locations]);
  const filtered = useMemo(() => {
    return sortedNormalisedLocations
      ?.sort((a, b) => a.name.localeCompare(b.name))
      .filter((location) => {
        return filterQuery.every((filter) => {
          if (!filter.value) return true;
          switch (filter.id) {
            case 'name':
              return location.nameLower.includes((filter.value as string).toLowerCase());
            case 'postcode':
              return location.postcodeLower.includes((filter.value as string).toLowerCase());
            case 'active':
              return location.active === (filter.value === 'true');
            default:
              return true;
          }
        });
      })
      .map((location) => ({
        key: location.uid,
        data: location,
      }));
  }, [filterQuery, sortedNormalisedLocations]);
  const totalPages = useMemo(() => Math.ceil(filtered.length / pageSize), [filtered.length]);
  const pagedLocations = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  useEffect(() => {
    setSelectedRows([]);
  }, [locations]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (location: ILocationDao) => {
    return <Checkbox isChecked={selectedRows.includes(location.uid)} onChange={(e) => rowChecked(e, location.uid)} />;
  };

  const actionCellTemplate = (location: ILocationDao) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => navigate(location.uid)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='View'
      />
      <SharedButton
        onClick={() => dialog?.openDialog(<UpdateLocationDialog location={location} />)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Update'
      />
      <SharedButton
        onClick={() =>
          dialog?.openDialog(
            <ConfirmationDialog
              title='Delete location'
              textContent='Are you sure you want to delete this location? This action cannot be undone.'
              action={() => deleteItem(LocationsApiService, location, flags, dialog)}
              buttonLabel='Delete Location'
            />
          )
        }
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Delete'
      />
    </div>
  );

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: actionCellTemplate,
      id: 'action',
    },
    {
      template: (location: ILocationDao) => <p>{location.address.fullAddress.postcode}</p>,
      id: 'postcode',
    },
    { template: selectCellTemplate, id: 'select' },
  ];

  const headerActions: PageElement[] = [
    {
      key: 'exportLocations',
      element: (
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ExportDialog
                title='Export Locations'
                apiService={LocationsApiService}
                functionName={FirebaseFunctionNames.EXPORT_LOCATIONS}
                filterQuery={filterQuery}
              />
            )
          }
          type='button'
          appearance='default'
          label='Export Locations'
        />
      ),
    },
    ...(selectedRows.length
      ? [
          {
            key: 'deleteLocations',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete locations'
                      textContent='Are you sure you want to delete these locations? This action cannot be undone.'
                      action={() => deleteItems(LocationsApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Locations'
                    />
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Vehicles'
              />
            ),
          },
        ]
      : [
          {
            key: 'addLocation',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<AddLocationDialog />)}
                type='button'
                appearance='primary'
                label='Add Location'
              />
            ),
          },
        ]),
  ];

  const filters: Filter[] = [
    {
      id: 'name',
      label: 'Location',
    },
    { id: 'postcode', label: 'Postcode' },
    {
      id: 'active',
      label: 'Status',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ];

  return (
    <>
      <PageHeader title='Locations' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={LocationsTableColumns}
        customTemplates={customTemplates}
        rows={pagedLocations}
        isLoading={isLoading}
      />
      <div className='w-full flex justify-end'>
        <div className='mt-4 w-fit'>
          <SharedButton
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            type='button'
            appearance='default'
            disabled={page === 1}
            label='Previous'
          />
          <span className='mx-4 text-sm'>
            Page {page} of {totalPages}
          </span>
          <SharedButton
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            type='button'
            appearance='default'
            disabled={page === totalPages}
            label='Next'
          />
        </div>
      </div>
    </>
  );
};

export default LocationsList;
